import * as XLSX from 'xlsx';

// Type definitions for ProductStats data structures
interface ItemStat {
    item_name: string;
    category_id: number;
    category_name: string;
    total_quantity_sold: number;
    total_monetary_value: number;
    total_percentage: number;
    total_granel_amount?: number;
    granel_orders_count?: number;
    unidad_medida_id?: number | null;
    unidad_medida_breakdown?: {
        [unidadMedidaId: string]: {
            granel_amount: number;
            orders_count: number;
        };
    };
    by_pedido_type?: {
        [pedidoTypeId: string]: {
            name: string;
            quantity: number;
            monetary_value: number;
            granel_amount?: number;
            granel_orders_count?: number;
        };
    };
    by_user?: {
        [userId: string]: {
            username: string;
            quantity: number;
            monetary_value: number;
            granel_amount?: number;
            granel_orders_count?: number;
        };
    };
    by_third_party?: {
        [tppId: string]: {
            provider_name: string;
            quantity: number;
            monetary_value: number;
            granel_amount?: number;
            granel_orders_count?: number;
        };
    };
}

interface GenericStat {
    name?: string;
    username?: string;
    category_name?: string;
    provider_name?: string;
    total_quantity_sold: number;
    total_monetary_value: number;
    total_percentage: number;
    total_granel_amount?: number;
    granel_orders_count?: number;
}

type AggregationType = 'by_item' | 'by_user' | 'by_pedido_type' | 'by_menu_category';
type TimeRange = 'current' | '7d' | '30d' | 'custom';

// Helper functions
const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('es-CO', {
        style: 'currency',
        currency: 'COP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(amount);
};

const getUnitLabel = (unidadMedidaId: number | null | undefined): string => {
    if (!unidadMedidaId) return '';
    
    const unitMapping: { [key: number]: string } = {
        1: 'kg', 2: 'gr', 3: 'L', 4: 'mL', 5: 'u', 6: 'pk', 
        7: 'cj', 8: 'dcn', 9: 'cm', 10: 'm', 11: 'm²', 
        12: 'lb', 13: 'lb', 14: 'u', 15: 'fl oz'
    };
    
    return unitMapping[unidadMedidaId] || 'u';
};

const formatGranelAmount = (amount: number, unidadMedidaId?: number | null): string => {
    if (amount === 0) return '';
    
    const unit = getUnitLabel(unidadMedidaId);
    const formattedAmount = amount % 1 === 0 ? amount.toString() : amount.toFixed(2);
    
    return `${formattedAmount} ${unit}`;
};

const toTitleCase = (str: string): string => {
    if (!str) return '';
    return str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
};

// Transform data for "Por Producto" tab
const transformProductData = (data: any[]): any[] => {
    return data.map((item: ItemStat, index: number) => {
        const hasGranel = (item.total_granel_amount || 0) > 0;
        const hasRegular = (item.total_quantity_sold || 0) > 0;
        
        let quantityDisplay = '';
        if (hasGranel && hasRegular) {
            quantityDisplay = `${item.total_quantity_sold} unidades + ${formatGranelAmount(item.total_granel_amount || 0, item.unidad_medida_id)}`;
        } else if (hasGranel) {
            quantityDisplay = `${formatGranelAmount(item.total_granel_amount || 0, item.unidad_medida_id)} (${item.granel_orders_count || 0} órdenes)`;
        } else {
            quantityDisplay = `${item.total_quantity_sold || 0} unidades`;
        }

        return {
            'Ranking': index + 1,
            'Producto': toTitleCase(item.item_name || ''),
            'Categoría': toTitleCase(item.category_name === 'Uncategorized' || !item.category_name ? 'Sin categoría' : item.category_name),
            'Cantidad Vendida': quantityDisplay,
            'Unidades Regulares': item.total_quantity_sold || 0,
            'Cantidad A Granel': item.total_granel_amount ? formatGranelAmount(item.total_granel_amount, item.unidad_medida_id) : '',
            'Órdenes A Granel': item.granel_orders_count || 0,
            'Valor Total': formatCurrency(item.total_monetary_value || 0),
            'Valor Numérico': item.total_monetary_value || 0,
            'Porcentaje Contribución': `${(item.total_percentage || 0).toFixed(2)}%`,
            'Porcentaje Numérico': item.total_percentage || 0
        };
    });
};

// Transform data for "Por Categoría" tab  
const transformCategoryData = (data: any[]): any[] => {
    return data.map((item: GenericStat, index: number) => {
        const hasGranel = (item.total_granel_amount || 0) > 0;
        const hasRegular = (item.total_quantity_sold || 0) > 0;
        
        let quantityDisplay = '';
        if (hasGranel && hasRegular) {
            quantityDisplay = `${item.total_quantity_sold} unidades + ${item.granel_orders_count || 0} órdenes a granel`;
        } else if (hasGranel) {
            quantityDisplay = `${item.granel_orders_count || 0} órdenes a granel`;
        } else {
            quantityDisplay = `${item.total_quantity_sold || 0} unidades`;
        }

        return {
            'Ranking': index + 1,
            'Categoría': toTitleCase(item.category_name || 'Sin categoría'),
            'Cantidad Vendida': quantityDisplay,
            'Unidades Regulares': item.total_quantity_sold || 0,
            'Órdenes A Granel': item.granel_orders_count || 0,
            'Valor Total': formatCurrency(item.total_monetary_value || 0),
            'Valor Numérico': item.total_monetary_value || 0,
            'Porcentaje Contribución': `${(item.total_percentage || 0).toFixed(2)}%`,
            'Porcentaje Numérico': item.total_percentage || 0
        };
    });
};

// Transform data for "Por Usuario" tab
const transformUserData = (data: any[]): any[] => {
    return data.map((item: GenericStat, index: number) => {
        const hasGranel = (item.total_granel_amount || 0) > 0;
        const hasRegular = (item.total_quantity_sold || 0) > 0;
        
        let quantityDisplay = '';
        if (hasGranel && hasRegular) {
            quantityDisplay = `${item.total_quantity_sold} unidades + ${item.granel_orders_count || 0} órdenes a granel`;
        } else if (hasGranel) {
            quantityDisplay = `${item.granel_orders_count || 0} órdenes a granel`;
        } else {
            quantityDisplay = `${item.total_quantity_sold || 0} unidades`;
        }

        return {
            'Ranking': index + 1,
            'Usuario': toTitleCase(item.username || 'Sin asignar'),
            'Cantidad Vendida': quantityDisplay,
            'Unidades Regulares': item.total_quantity_sold || 0,
            'Órdenes A Granel': item.granel_orders_count || 0,
            'Valor Total': formatCurrency(item.total_monetary_value || 0),
            'Valor Numérico': item.total_monetary_value || 0,
            'Porcentaje Contribución': `${(item.total_percentage || 0).toFixed(2)}%`,
            'Porcentaje Numérico': item.total_percentage || 0
        };
    });
};

// Transform data for "Por Canal" tab
const transformChannelData = (data: any[]): any[] => {
    return data.map((item: GenericStat, index: number) => {
        const hasGranel = (item.total_granel_amount || 0) > 0;
        const hasRegular = (item.total_quantity_sold || 0) > 0;
        
        let quantityDisplay = '';
        if (hasGranel && hasRegular) {
            quantityDisplay = `${item.total_quantity_sold} unidades + ${item.granel_orders_count || 0} órdenes a granel`;
        } else if (hasGranel) {
            quantityDisplay = `${item.granel_orders_count || 0} órdenes a granel`;
        } else {
            quantityDisplay = `${item.total_quantity_sold || 0} unidades`;
        }

        return {
            'Ranking': index + 1,
            'Canal de Venta': toTitleCase(item.name || 'Sin definir'),
            'Cantidad Vendida': quantityDisplay,
            'Unidades Regulares': item.total_quantity_sold || 0,
            'Órdenes A Granel': item.granel_orders_count || 0,
            'Valor Total': formatCurrency(item.total_monetary_value || 0),
            'Valor Numérico': item.total_monetary_value || 0,
            'Porcentaje Contribución': `${(item.total_percentage || 0).toFixed(2)}%`,
            'Porcentaje Numérico': item.total_percentage || 0
        };
    });
};

// Main export function
export const exportProductStatsToExcel = async (
    currentAggregationType: AggregationType,
    timeRange: TimeRange,
    localId: number,
    fileName?: string
) => {
    try {
        // Import API functions dynamically to avoid circular imports
        const { GetStatsForCurrentSession, GetStatsByRange } = await import('../../../../../crud/menu_api/item_stats');
        
        const requestBody = { local_id: localId, aggregation_type: 'by_item' as AggregationType };

        // Fetch data for all aggregation types
        const aggregationTypes: AggregationType[] = ['by_item', 'by_menu_category', 'by_user', 'by_pedido_type'];
        
        const dataPromises = aggregationTypes.map(async (aggType) => {
            const request = { ...requestBody, aggregation_type: aggType };
            
            if (timeRange === 'current') {
                return GetStatsForCurrentSession(request);
            } else {
                const endDate = new Date();
                const startDate = new Date();
                startDate.setDate(endDate.getDate() - (timeRange === '7d' ? 7 : 30));
                
                return GetStatsByRange({ 
                    ...request, 
                    start_date: startDate.toISOString().slice(0, 10), 
                    end_date: endDate.toISOString().slice(0, 10)
                });
            }
        });

        const responses: any[] = await Promise.all(dataPromises);
        
        // Process data for each tab
        const workbook = XLSX.utils.book_new();
        
        // Helper function to sort data by monetary value
        const sortDataByValue = (data: any) => {
            if (!data || typeof data !== 'object') return [];
            return Object.entries(data).sort(([, a], [, b]) => (b as any).total_monetary_value - (a as any).total_monetary_value);
        };

        // Tab 1: Por Producto
        const productData = sortDataByValue(responses[0].data || {});
        if (productData.length > 0) {
            const transformedProductData = transformProductData(productData.map(([, item]) => item));
            const productWs = XLSX.utils.json_to_sheet(transformedProductData);
            
            // Set column widths for product tab
            productWs['!cols'] = [
                { wch: 8 },   // Ranking
                { wch: 25 },  // Producto
                { wch: 15 },  // Categoría
                { wch: 20 },  // Cantidad Vendida
                { wch: 12 },  // Unidades Regulares
                { wch: 15 },  // Cantidad A Granel
                { wch: 12 },  // Órdenes A Granel
                { wch: 15 },  // Valor Total
                { wch: 12 },  // Valor Numérico
                { wch: 18 },  // Porcentaje Contribución
                { wch: 12 }   // Porcentaje Numérico
            ];
            
            XLSX.utils.book_append_sheet(workbook, productWs, 'Por Producto');
        }

        // Tab 2: Por Categoría
        const categoryData = sortDataByValue(responses[1].data || {});
        if (categoryData.length > 0) {
            const transformedCategoryData = transformCategoryData(categoryData.map(([, item]) => item));
            const categoryWs = XLSX.utils.json_to_sheet(transformedCategoryData);
            
            categoryWs['!cols'] = [
                { wch: 8 },   // Ranking
                { wch: 20 },  // Categoría
                { wch: 20 },  // Cantidad Vendida
                { wch: 12 },  // Unidades Regulares
                { wch: 12 },  // Órdenes A Granel
                { wch: 15 },  // Valor Total
                { wch: 12 },  // Valor Numérico
                { wch: 18 },  // Porcentaje Contribución
                { wch: 12 }   // Porcentaje Numérico
            ];
            
            XLSX.utils.book_append_sheet(workbook, categoryWs, 'Por Categoría');
        }

        // Tab 3: Por Usuario
        const userData = sortDataByValue(responses[2].data || {});
        if (userData.length > 0) {
            const transformedUserData = transformUserData(userData.map(([, item]) => item));
            const userWs = XLSX.utils.json_to_sheet(transformedUserData);
            
            userWs['!cols'] = [
                { wch: 8 },   // Ranking
                { wch: 20 },  // Usuario
                { wch: 20 },  // Cantidad Vendida
                { wch: 12 },  // Unidades Regulares
                { wch: 12 },  // Órdenes A Granel
                { wch: 15 },  // Valor Total
                { wch: 12 },  // Valor Numérico
                { wch: 18 },  // Porcentaje Contribución
                { wch: 12 }   // Porcentaje Numérico
            ];
            
            XLSX.utils.book_append_sheet(workbook, userWs, 'Por Usuario');
        }

        // Tab 4: Por Canal
        const channelData = sortDataByValue(responses[3].data || {});
        if (channelData.length > 0) {
            const transformedChannelData = transformChannelData(channelData.map(([, item]) => item));
            const channelWs = XLSX.utils.json_to_sheet(transformedChannelData);
            
            channelWs['!cols'] = [
                { wch: 8 },   // Ranking
                { wch: 20 },  // Canal de Venta
                { wch: 20 },  // Cantidad Vendida
                { wch: 12 },  // Unidades Regulares
                { wch: 12 },  // Órdenes A Granel
                { wch: 15 },  // Valor Total
                { wch: 12 },  // Valor Numérico
                { wch: 18 },  // Porcentaje Contribución
                { wch: 12 }   // Porcentaje Numérico
            ];
            
            XLSX.utils.book_append_sheet(workbook, channelWs, 'Por Canal');
        }

        // Generate filename
        const timeRangeLabel = timeRange === 'current' ? 'Sesion_Actual' : 
                              timeRange === '7d' ? '7_Dias' : '30_Dias';
        
        const defaultFileName = `Estadisticas_Productos_${timeRangeLabel}_${new Date().toISOString().split('T')[0]}.xlsx`;
        const finalFileName = fileName || defaultFileName;

        // Download the file
        XLSX.writeFile(workbook, finalFileName);

        return true;
    } catch (error) {
        console.error('Error exporting product stats to Excel:', error);
        throw error;
    }
};
